<script setup lang="ts">
// スマートオブジェクトのプロパティ: 元画像サイズ、補間、変形リセット、内容の置き換え、ラスタライズ
import { computed } from 'vue'
import type { SmartLayer } from '@/types/document'
import { useDocumentStore } from '@/stores/documentStore'
import { useI18n } from '@/i18n'
import { runCommand } from '@/commands/registry'
import { pickFiles } from '@/io/download'
import { decodeImageFile } from '@/io/imageDecode'
import { toast } from '@/composables/useToast'

const props = defineProps<{ layer: SmartLayer }>()
const docStore = useDocumentStore()
const { t } = useI18n()
const rect = computed(() => {
  void docStore.historyVersion
  return docStore.smartRect(props.layer.id)
})
const scale = computed(() => {
  const m = props.layer.transform
  return { x: Math.hypot(m[0], m[1]), y: Math.hypot(m[3], m[4]), angle: (Math.atan2(m[1], m[0]) * 180) / Math.PI }
})
async function replace() {
  const files = await pickFiles('image/*')
  const f = files[0]
  if (!f) return
  try {
    const img = await decodeImageFile(f)
    docStore.replaceSmartContents(props.layer.id, img.width, img.height, img.rgba)
  } catch (e) {
    toast.error(e instanceof Error ? e.message : String(e))
  }
}
</script>

<template>
  <div class="smart">
    <div class="field"><span class="muted">{{ t('smart.source') }}</span><span class="mono">{{ layer.sourceRect.w }} × {{ layer.sourceRect.h }}</span></div>
    <div v-if="rect" class="field"><span class="muted">W × H</span><span class="mono">{{ rect.w }} × {{ rect.h }}</span></div>
    <div class="field"><span class="muted">X / Y</span><span class="mono">{{ Math.round(layer.transform[6]) }}, {{ Math.round(layer.transform[7]) }}</span></div>
    <div class="field"><span class="muted">{{ t('transform.scale') }}</span><span class="mono">{{ Math.round(scale.x * 100) }}% × {{ Math.round(scale.y * 100) }}% / {{ Math.round(scale.angle * 10) / 10 }}°</span></div>
    <label class="field"><span class="muted">{{ t('smart.interpolation') }}</span>
      <select :value="layer.interpolation" @change="runCommand('layer.setSmart', { id: layer.id, interpolation: ($event.target as HTMLSelectElement).value })">
        <option value="nearest">nearest</option>
        <option value="bilinear">bilinear</option>
        <option value="bicubic">bicubic</option>
      </select>
    </label>
    <div class="row wrap">
      <button @click="runCommand('layer.setSmart', { id: layer.id, reset: true })">{{ t('smart.reset') }}</button>
      <button @click="replace">{{ t('smart.replace') }}</button>
      <button @click="runCommand('layer.rasterizeSmart', { id: layer.id })">{{ t('smart.rasterize') }}</button>
    </div>
    <p class="muted small">{{ t('smart.hint') }}</p>
  </div>
</template>

<style scoped>
.smart {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.wrap {
  flex-wrap: wrap;
  gap: 4px;
}
.small {
  font-size: 10px;
  margin: 0;
}
</style>
